/**
 * Kontrola faktur SFD - czy wszystkie faktury z portalu b2b.sfd.pl (wybrany
 * zakres dat) sa dodane w V3SYS, ktore brakuja i od kiedy; druga strona: co jest
 * tylko w systemie, duplikaty, zly sklep. Kontrakt: scratchpad/kontrola/KONTRAKT.md.
 *
 * Dane: GET /kontrola-faktur/podsumowanie (kafelki, per sklep, kto dodawal,
 * per miesiac) + GET /kontrola-faktur/lista (tabela, filtr/sklep/szukaj/strona),
 * oba z zakresem od/do. "Odśwież z SFD" = POST /odswiez {tryb:'biezace'}
 * (synchronicznie); "Pobierz całą historię…" = {tryb:'pelna_historia'} -> 202
 * {sync_id} i odpytywanie GET /odswiez/status/<id> co 3 s do ok/blad.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { kontrolaFakturService } from '../services/kontrolaFakturService';
import {
  PodsumowanieKontroli, FiltryKontroli, TabelaKontroli, KtoDodawal, PerMiesiac, PerSklep, ZakresDat, PasekWz, PasekParagonow,
  formatujDate, formatujDateCzas, domyslnyZakres, OD_DOMYSLNE, OD_CALEJ_HISTORII, dzisISO,
} from '../components/kontrola-faktur';

const NA_STRONE = 100;
const ODPYTUJ_CO_MS = 3000;
const MAX_BLEDOW_STATUSU = 5;

/** Komunikat bledu odswiezania po kodzie z kontraktu. */
const komunikatBledu = (err) => {
  switch (err?.errorCode) {
    case 'SFD_BRAK_KONFIGURACJI': return 'Brak konfiguracji SFD B2B (e-mail/hasło). Uzupełnij w ustawieniach admina.';
    case 'SFD_LOGOWANIE': return 'Portal SFD odrzucił logowanie — sprawdź e-mail i hasło w ustawieniach admina.';
    case 'SFD_PORTAL': return `Błąd portalu SFD: ${err.message || 'nieznany'}`;
    case 'SYNC_W_TOKU': return 'Inna synchronizacja jeszcze trwa — spróbuj za chwilę.';
    default: return err?.message || 'Nie udało się odświeżyć danych z SFD.';
  }
};

const btnPrimary = { padding: '6px 12px', fontSize: '12px', fontWeight: 600, borderRadius: '4px', cursor: 'pointer', border: '1px solid var(--primary)', background: 'var(--primary)', color: '#fff' };
const btnSecondary = { padding: '6px 12px', fontSize: '12px', fontWeight: 600, borderRadius: '4px', cursor: 'pointer', border: '1px solid var(--border)', background: 'var(--card-bg)', color: 'var(--text)' };
const btnDanger = { ...btnPrimary, border: '1px solid var(--danger)', background: 'var(--danger)' };

/** Okno potwierdzenia pobrania pelnej historii (data "od" do zmiany, domyslnie OD_DOMYSLNE). */
const ModalPelnaHistoria = ({ od, onPotwierdz, onZamknij }) => {
  const [odWybrane, setOdWybrane] = useState(od);
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '2rem' }}>
      <div role="dialog" aria-modal="true" aria-labelledby="modal-historia-tytul" data-testid="modal-pelna-historia"
        style={{ backgroundColor: 'var(--card-bg)', borderRadius: '8px', width: '100%', maxWidth: '480px', padding: '1.25rem', fontSize: '12px' }}>
        <h5 id="modal-historia-tytul" style={{ margin: '0 0 8px', fontSize: '15px', fontWeight: 700 }}>Pobrać całą historię z SFD?</h5>
        <p style={{ margin: '0 0 12px', color: 'var(--text)' }}>
          Pobierze wszystkie faktury z portalu od {formatujDate(odWybrane)} (kilka tysięcy dokumentów, kilka minut). Kontynuować?
        </p>
        <label style={{ display: 'block', marginBottom: '12px', fontSize: '11px', color: 'var(--text-secondary)' }}>
          Pobierz od{' '}
          <input type="date" aria-label="Historia od" value={odWybrane} min={OD_CALEJ_HISTORII} max={dzisISO()}
            onChange={(e) => e.target.value && setOdWybrane(e.target.value)}
            style={{ padding: '4px 6px', fontSize: '11px', border: '1px solid var(--border)', borderRadius: '4px', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }} />
        </label>
        <p style={{ margin: '0 0 12px', fontSize: '11px', color: 'var(--text-muted)' }}>
          Pobieranie idzie w tle na serwerze — możesz zostać na tej stronie, postęp będzie widoczny na pasku.
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button type="button" onClick={onZamknij} style={btnSecondary}>Anuluj</button>
          <button type="button" onClick={() => onPotwierdz(odWybrane)} style={btnDanger} data-testid="btn-potwierdz-historie">Pobierz całą historię</button>
        </div>
      </div>
    </div>
  );
};

/** Pasek postepu pelnej historii (odswiezany co 3 s ze statusu). */
const PostepSynchronizacji = ({ postep }) => {
  const pobrane = postep?.adresy_pobrane ?? 0;
  const doPobrania = postep?.adresy_do_pobrania ?? 0;
  const razem = pobrane + doPobrania;
  const procent = razem > 0 ? Math.min(100, Math.round((pobrane / razem) * 100)) : null;
  return (
    <div data-testid="postep-sync" style={{ marginTop: '8px', padding: '8px 10px', borderRadius: '4px', backgroundColor: 'var(--info-light)', color: 'var(--info)', fontSize: '11px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap', marginBottom: '6px' }}>
        <span style={{ fontWeight: 600 }}>Pobieranie całej historii z SFD w toku…</span>
        <span>
          stron {postep?.stron ?? 0} · pobrano {postep?.pobrano ?? 0} · nowych {postep?.nowych ?? 0} · adresów {pobrane}{razem > 0 ? ` / ${razem}` : ''}
          {procent !== null ? ` (${procent}%)` : ''}
        </span>
      </div>
      <div style={{ height: '6px', backgroundColor: 'var(--border)', borderRadius: '3px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: procent === null ? '100%' : `${procent}%`, backgroundColor: 'var(--info)', opacity: procent === null ? 0.4 : 1, transition: 'width 0.3s ease' }} />
      </div>
    </div>
  );
};

const KontrolaFakturPage = () => {
  const [zakres, setZakres] = useState(domyslnyZakres);
  const [filtr, setFiltr] = useState('brak');
  const [szukaj, setSzukaj] = useState('');
  const [szukajOdlozony, setSzukajOdlozony] = useState('');
  const [sklepId, setSklepId] = useState(null);   // null = wszystkie; 0 = nieznany adres (ANEKS 1)
  // Seria UFR to faktury KOSZTOWE (nie towarowe) - domyslnie poza kontrola (wlasciciel 05.09)
  const [bezUfr, setBezUfr] = useState(true);
  const [strona, setStrona] = useState(1);

  const [podsumowanie, setPodsumowanie] = useState(null);
  const [lista, setLista] = useState({ wiersze: [], razem: 0, strona: 1, na_strone: NA_STRONE });
  const [ladowaniePods, setLadowaniePods] = useState(true);
  const [ladowanieListy, setLadowanieListy] = useState(true);
  const [blad, setBlad] = useState(null);
  const [odswiezanie, setOdswiezanie] = useState(false);
  const [wynikOdswiezania, setWynikOdswiezania] = useState(null);
  const [modalHistoria, setModalHistoria] = useState(false);
  const [syncId, setSyncId] = useState(null);        // id pelnej historii w toku (odpytywanie)
  const [pobieranieId, setPobieranieId] = useState(null);   // wiersz, dla ktorego trwa "Pobierz plik"
  const [pobieraniePdfId, setPobieraniePdfId] = useState(null);   // wiersz, dla ktorego trwa "PDF z portalu"
  const [postep, setPostep] = useState(null);
  const listaReq = useRef(0);

  // Szukanie na zywo - z opoznieniem, zeby nie strzelac zapytaniem po kazdej literze
  useEffect(() => {
    const t = setTimeout(() => setSzukajOdlozony(szukaj.trim()), 300);
    return () => clearTimeout(t);
  }, [szukaj]);

  const ladujPodsumowanie = useCallback(async () => {
    setLadowaniePods(true);
    try {
      const dane = await kontrolaFakturService.podsumowanie({ od: zakres.od, do: zakres.do, wyklucz_ufr: bezUfr ? 1 : 0 });
      setPodsumowanie(dane || null);
      setBlad(null);
    } catch (err) {
      if (!err?.isAuthError) setBlad(err?.message || 'Nie udało się pobrać podsumowania');
    } finally {
      setLadowaniePods(false);
    }
  }, [zakres.od, zakres.do, bezUfr]);

  const ladujListe = useCallback(async () => {
    const nr = ++listaReq.current;
    setLadowanieListy(true);
    try {
      const params = { filtr, od: zakres.od, do: zakres.do, szukaj: szukajOdlozony, strona, na_strone: NA_STRONE, wyklucz_ufr: bezUfr ? 1 : 0 };
      if (sklepId !== null && sklepId !== undefined) params.sklep_id = sklepId;
      const dane = await kontrolaFakturService.lista(params);
      if (nr !== listaReq.current) return; // nowsze zapytanie juz wyszlo
      setLista({
        wiersze: Array.isArray(dane?.wiersze) ? dane.wiersze : [],
        razem: dane?.razem ?? 0,
        strona: dane?.strona ?? strona,
        na_strone: dane?.na_strone ?? NA_STRONE,
      });
    } catch (err) {
      if (nr !== listaReq.current) return;
      if (!err?.isAuthError) setBlad(err?.message || 'Nie udało się pobrać listy');
    } finally {
      if (nr === listaReq.current) setLadowanieListy(false);
    }
  }, [filtr, zakres.od, zakres.do, szukajOdlozony, strona, sklepId, bezUfr]);

  useEffect(() => { ladujPodsumowanie(); }, [ladujPodsumowanie]);
  useEffect(() => { ladujListe(); }, [ladujListe]);

  const zmienFiltr = (nowy) => { setFiltr(nowy); setStrona(1); };
  const zmienSzukaj = (v) => { setSzukaj(v); setStrona(1); };
  const zmienZakres = (z) => { setZakres(z); setStrona(1); };
  const zmienSklep = (id) => { setSklepId(id); setStrona(1); };
  // Klik w wiersz "Per sklep": ten sklep + brakujace faktury
  const wybierzSklepZTabeli = (id) => { setSklepId(id); setFiltr('brak'); setStrona(1); };

  /** "Pobierz plik" (ANEKS 3): POST plik/pobierz dla jednego wiersza, potem podmiana wiersza w liscie. */
  const pobierzPlik = async (wiersz) => {
    if (!wiersz?.id) return;
    setPobieranieId(wiersz.id);
    try {
      const wynik = await kontrolaFakturService.pobierzPlikTeraz(wiersz.id);
      const plik = wynik?.plik || null;
      const moznaDodac = wynik?.mozna_dodac !== undefined
        ? Boolean(wynik.mozna_dodac)
        : (plik?.status === 'pobrany' && ['brak', 'czesciowo_wz', 'dodana_jako_wz'].includes(wiersz.status));
      setLista(prev => ({ ...prev, wiersze: prev.wiersze.map(w => (w.id === wiersz.id ? { ...w, plik: plik || w.plik, mozna_dodac: moznaDodac } : w)) }));
      if (plik?.status === 'pobrany') toast.success(`Pobrano plik ${plik.nazwa || ''} z SFD`);
      else if (plik?.status === 'blad') toast.error(`Plik z SFD ma błąd: ${plik.blad || 'nieznany'}`);
      else toast(`Plik: ${plik?.status || 'brak informacji'}`);
    } catch (err) {
      if (err?.errorCode === 'PLIK_BRAK_W_PORTALU') {
        setLista(prev => ({ ...prev, wiersze: prev.wiersze.map(w => (w.id === wiersz.id ? { ...w, plik: { ...(w.plik || {}), status: 'brak_w_portalu' } } : w)) }));
        toast.error('Portal SFD nie ma pliku dla tej faktury');
      } else if (!err?.isAuthError) {
        toast.error(komunikatBledu(err));
      }
    } finally {
      setPobieranieId(null);
    }
  };

  /** "PDF z portalu" (2026-09-13): wydruk faktury z portalu obok wiersza -> plik.pdf = true (przycisk "PDF" = podglad). */
  const pdfZPortalu = async (wiersz) => {
    if (!wiersz?.id) return;
    setPobieraniePdfId(wiersz.id);
    try {
      const wynik = await kontrolaFakturService.pobierzPdfZPortalu(wiersz.id);
      const plik = wynik?.plik || { ...(wiersz.plik || {}), pdf: true };
      setLista(prev => ({ ...prev, wiersze: prev.wiersze.map(w => (w.id === wiersz.id ? { ...w, plik } : w)) }));
      toast.success(`Pobrano PDF faktury ${wiersz.numer || ''} z portalu SFD`);
    } catch (err) {
      if (!err?.isAuthError) toast.error(err?.data?.message || komunikatBledu(err));
    } finally {
      setPobieraniePdfId(null);
    }
  };

  /** Wynik zakonczonej synchronizacji (biezacej albo pelnej) -> pasek, toast, przeladowanie. */
  const pokazWynik = useCallback(async (wynik) => {
    setWynikOdswiezania(wynik);
    const adresy = wynik?.adresy_pobrane !== undefined ? `, adresów: ${wynik.adresy_pobrane}${wynik.adresy_bledy ? ` (błędów ${wynik.adresy_bledy})` : ''}` : '';
    toast.success(`Pobrano ${wynik?.pobrano ?? 0} dokumentów z SFD (nowych: ${wynik?.nowych ?? 0}, zmienionych: ${wynik?.zmienionych ?? 0}, ${wynik?.stron ?? 0} stron${adresy}${wynik?.czas_s !== undefined ? `, ${wynik.czas_s} s` : ''})`);
    await Promise.all([ladujPodsumowanie(), ladujListe()]);
  }, [ladujPodsumowanie, ladujListe]);

  const pokazBlad = (err) => {
    if (!err?.isAuthError) toast.error(komunikatBledu(err));
    setWynikOdswiezania({ status: 'blad', komunikat: komunikatBledu(err), errorCode: err?.errorCode });
  };

  /** Biezace okno (od = poczatek zakresu) - synchronicznie. */
  const odswiez = async () => {
    setOdswiezanie(true);
    setWynikOdswiezania(null);
    try {
      const wynik = await kontrolaFakturService.odswiez('biezace', zakres.od);
      if (wynik?.status === 'w_toku' && wynik?.sync_id) {
        // backend moze tez odpowiedziec 202 - wtedy odpytujemy jak przy pelnej historii
        setPostep(wynik);
        setSyncId(wynik.sync_id);
      } else {
        await pokazWynik(wynik);
      }
    } catch (err) {
      pokazBlad(err);
    } finally {
      setOdswiezanie(false);
    }
  };

  /** Pelna historia - 202 + odpytywanie statusu. */
  const pobierzPelnaHistorie = async (od = OD_DOMYSLNE) => {
    setModalHistoria(false);
    setOdswiezanie(true);
    setWynikOdswiezania(null);
    try {
      const wynik = await kontrolaFakturService.odswiez('pelna_historia', od);
      if (wynik?.sync_id && wynik?.status !== 'ok' && wynik?.status !== 'blad') {
        setPostep({ ...wynik, status: 'w_toku' });
        setSyncId(wynik.sync_id);
      } else if (wynik?.status === 'blad') {
        pokazBlad(new Error(wynik.komunikat || 'Synchronizacja zakończyła się błędem'));
      } else {
        await pokazWynik(wynik);
      }
    } catch (err) {
      pokazBlad(err);
    } finally {
      setOdswiezanie(false);
    }
  };

  // Odpytywanie statusu pelnej historii co 3 s, az ok/blad
  useEffect(() => {
    if (!syncId) return undefined;
    let aktywny = true;
    let timer = null;
    let bledy = 0;
    const zakoncz = () => { setSyncId(null); setPostep(null); };
    const tick = async () => {
      try {
        const st = await kontrolaFakturService.statusOdswiezania(syncId);
        if (!aktywny) return;
        if (st?.status === 'ok') {
          zakoncz();
          await pokazWynik({ ...st, sync_id: syncId });
          return;
        }
        if (st?.status === 'blad') {
          zakoncz();
          const komunikat = st.komunikat || 'Synchronizacja zakończyła się błędem';
          toast.error(komunikat);
          setWynikOdswiezania({ status: 'blad', komunikat });
          await Promise.all([ladujPodsumowanie(), ladujListe()]);
          return;
        }
        bledy = 0;
        setPostep(prev => ({ ...(prev || {}), ...(st || {}), status: 'w_toku' }));
      } catch (err) {
        if (!aktywny) return;
        bledy += 1;
        if (err?.isAuthError || bledy >= MAX_BLEDOW_STATUSU) {
          zakoncz();
          setWynikOdswiezania({ status: 'blad', komunikat: `Nie udało się sprawdzić stanu synchronizacji (${err?.message || 'błąd'}). Odśwież stronę za chwilę.` });
          return;
        }
      }
      if (aktywny) timer = setTimeout(tick, ODPYTUJ_CO_MS);
    };
    timer = setTimeout(tick, ODPYTUJ_CO_MS);
    return () => { aktywny = false; if (timer) clearTimeout(timer); };
  }, [syncId, pokazWynik, ladujPodsumowanie, ladujListe]);

  const zrodlo = podsumowanie?.zrodla?.sfd_b2b;
  const brakKonfiguracji = Boolean(podsumowanie) && (!zrodlo || zrodlo.wlaczone === false);
  const ostatniaSync = zrodlo?.ostatnia_sync;
  const razemStron = Math.max(1, Math.ceil((lista.razem || 0) / (lista.na_strone || NA_STRONE)));
  const zajety = odswiezanie || Boolean(syncId);
  // liczniki chipow: liczby + WZ bez faktury z sekcji `wz` (ANEKS 2)
  const liczby = podsumowanie?.liczby
    ? { ...podsumowanie.liczby, wz_bez_faktury: podsumowanie?.wz?.bez_faktury }
    : undefined;
  // sklepy do modalu "Dodaj z XML z maila" (ANEKS 6b) - znane sklepy z per_sklep
  const sklepyZnane = (Array.isArray(podsumowanie?.per_sklep) ? podsumowanie.per_sklep : [])
    .filter(s => s.sklep_id !== null && s.sklep_id !== undefined)
    .map(s => ({ id: s.sklep_id, nazwa: s.nazwa }));
  const przeladuj = () => { ladujPodsumowanie(); ladujListe(); };

  return (
    <div style={{ fontSize: '12px' }}>
      {modalHistoria && (
        <ModalPelnaHistoria od={OD_DOMYSLNE} onPotwierdz={pobierzPelnaHistorie} onZamknij={() => setModalHistoria(false)} />
      )}

      {/* Naglowek */}
      <div className="card" style={{ marginBottom: '12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
          <div>
            <h2 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px' }}>Kontrola faktur SFD</h2>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0 }}>
              Portal b2b.sfd.pl vs faktury zakupowe w V3SYS
              {podsumowanie?.zakres && <> · zakres {formatujDate(podsumowanie.zakres.od)} – {formatujDate(podsumowanie.zakres.do)}{podsumowanie.zakres.dni ? ` (${podsumowanie.zakres.dni} dni)` : ''}</>}
              {' · '}
              <span data-testid="ostatnia-sync">
                ostatnia synchronizacja: {ostatniaSync ? formatujDateCzas(ostatniaSync) : 'nigdy'}
                {zrodlo?.status === 'blad' && <span style={{ color: 'var(--danger)' }}> (błąd{zrodlo.komunikat ? `: ${zrodlo.komunikat}` : ''})</span>}
              </span>
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <button type="button" onClick={() => { ladujPodsumowanie(); ladujListe(); }} disabled={zajety} style={btnSecondary}>Przeładuj</button>
            <button type="button" onClick={odswiez} disabled={zajety || brakKonfiguracji} data-testid="btn-odswiez"
              title={`Pobierze faktury z portalu od ${formatujDate(zakres.od)} (bieżące okno)`}
              style={{ ...btnPrimary, opacity: zajety || brakKonfiguracji ? 0.6 : 1 }}>
              {odswiezanie ? 'Pobieram z SFD…' : 'Odśwież z SFD'}
            </button>
            <button type="button" onClick={() => setModalHistoria(true)} disabled={zajety || brakKonfiguracji} data-testid="btn-pelna-historia"
              title="Pobierze wszystkie faktury z portalu (w tle, kilka minut)"
              style={{ ...btnSecondary, border: '1px solid var(--primary)', color: 'var(--primary)', opacity: zajety || brakKonfiguracji ? 0.6 : 1 }}>
              Pobierz całą historię…
            </button>
          </div>
        </div>
        <div style={{ marginTop: '10px' }}>
          <ZakresDat zakres={zakres} onZmiana={zmienZakres} />
        </div>
        {syncId && <PostepSynchronizacji postep={postep} />}
        {wynikOdswiezania && (
          <div data-testid="wynik-odswiezania" style={{
            marginTop: '8px', padding: '6px 10px', borderRadius: '4px', fontSize: '11px',
            backgroundColor: wynikOdswiezania.status === 'ok' ? 'var(--success-light)' : 'var(--danger-light)',
            color: wynikOdswiezania.status === 'ok' ? 'var(--success)' : 'var(--danger)',
          }}>
            {wynikOdswiezania.status === 'ok'
              ? <>Synchronizacja OK: pobrano {wynikOdswiezania.pobrano}, nowych {wynikOdswiezania.nowych}{wynikOdswiezania.zmienionych !== undefined && <>, zmienionych {wynikOdswiezania.zmienionych}</>}, stron {wynikOdswiezania.stron}
                  {wynikOdswiezania.adresy_pobrane !== undefined && <>, adresów dostawy {wynikOdswiezania.adresy_pobrane}{wynikOdswiezania.adresy_bledy ? ` (błędów ${wynikOdswiezania.adresy_bledy})` : ''}</>}
                  {wynikOdswiezania.sklepy_nieznane > 0 && <span style={{ color: 'var(--warning)' }}>, nieznane adresy: {wynikOdswiezania.sklepy_nieznane} — <Link to="/admin?tab=sfd-b2b" style={{ color: 'var(--warning)', fontWeight: 600 }}>przypisz sklep</Link></span>}
                  {wynikOdswiezania.czas_s !== undefined && <>, czas {wynikOdswiezania.czas_s} s</>}</>
              : <>{wynikOdswiezania.komunikat}{' '}
                  {(wynikOdswiezania.errorCode === 'SFD_BRAK_KONFIGURACJI' || wynikOdswiezania.errorCode === 'SFD_LOGOWANIE') && (
                    <Link to="/admin?tab=sfd-b2b" style={{ color: 'var(--danger)', fontWeight: 600 }}>Przejdź do ustawień</Link>
                  )}
                </>}
          </div>
        )}
      </div>

      {/* Blad ladowania */}
      {blad && (
        <div data-testid="blad" style={{ padding: '8px 12px', backgroundColor: 'var(--danger-light)', color: 'var(--danger)', borderRadius: '4px', marginBottom: '12px', fontSize: '12px' }}>
          {'❌'} {blad}
        </div>
      )}

      {/* Zrodlo niewlaczone / brak konfiguracji */}
      {brakKonfiguracji && (
        <div data-testid="brak-konfiguracji" style={{ padding: '10px 12px', backgroundColor: 'var(--warning-light)', color: 'var(--warning)', borderRadius: '4px', marginBottom: '12px', fontSize: '12px', border: '1px solid var(--warning)' }}>
          <strong>Źródło SFD B2B nie jest włączone lub skonfigurowane.</strong>{' '}
          Podaj e-mail i hasło do portalu b2b.sfd.pl i włącz integrację w{' '}
          <Link to="/admin?tab=sfd-b2b" style={{ color: 'var(--warning)', fontWeight: 600, textDecoration: 'underline' }}>ustawieniach admina (zakładka SFD B2B)</Link>.
        </div>
      )}

      {/* Podsumowanie */}
      <div className="card" style={{ marginBottom: '12px' }}>
        {ladowaniePods && !podsumowanie
          ? <div style={{ color: 'var(--text-muted)', padding: '4px 0' }}>Ładowanie podsumowania…</div>
          : <PodsumowanieKontroli liczby={podsumowanie?.liczby} aktywnyFiltr={filtr} onFiltr={zmienFiltr} />}
        {podsumowanie?.wz && <PasekWz wz={podsumowanie.wz} aktywnyFiltr={filtr} onFiltr={zmienFiltr} />}
        {/* ANEKS 6: paragony Renomy - lista PDF + XML z maila */}
        <PasekParagonow liczby={podsumowanie?.liczby} aktywnyFiltr={filtr} onFiltr={zmienFiltr} onOdswiez={przeladuj} />
      </div>

      {/* Per sklep (wg adresu dostawy z SFD) */}
      <div style={{ marginBottom: '12px' }}>
        <PerSklep perSklep={podsumowanie?.per_sklep} aktywnySklepId={sklepId} onWybierz={wybierzSklepZTabeli} />
      </div>

      {/* Filtry + tabela */}
      <div className="card" style={{ marginBottom: '12px' }}>
        <div style={{ marginBottom: '10px' }}>
          <FiltryKontroli
            filtr={filtr} onFiltr={zmienFiltr} szukaj={szukaj} onSzukaj={zmienSzukaj} liczby={liczby}
            perSklep={podsumowanie?.per_sklep} sklepId={sklepId} onSklep={zmienSklep}
            bezUfr={bezUfr} onBezUfr={(v) => { setBezUfr(v); setStrona(1); }} wykluczoneUfr={liczby?.wykluczone_ufr}
          />
        </div>
        <TabelaKontroli wiersze={lista.wiersze} ladowanie={ladowanieListy} onPobierzPlik={pobierzPlik} pobieranieId={pobieranieId}
          onPdfZPortalu={pdfZPortalu} pobieraniePdfId={pobieraniePdfId}
          sklepy={sklepyZnane} onDodano={przeladuj} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px', fontSize: '11px', color: 'var(--text-muted)' }}>
          <span data-testid="licznik-wierszy">Razem: {lista.razem} · strona {lista.strona} z {razemStron}</span>
          <div style={{ display: 'flex', gap: '6px' }}>
            <button type="button" disabled={strona <= 1 || ladowanieListy} onClick={() => setStrona(s => Math.max(1, s - 1))} style={{ ...btnSecondary, padding: '4px 10px', fontSize: '11px' }}>‹ Poprzednia</button>
            <button type="button" disabled={strona >= razemStron || ladowanieListy} onClick={() => setStrona(s => s + 1)} style={{ ...btnSecondary, padding: '4px 10px', fontSize: '11px' }}>Następna ›</button>
          </div>
        </div>
      </div>

      {/* Kto dodawal / per miesiac */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '12px' }}>
        <KtoDodawal dodali={podsumowanie?.dodali} />
        <PerMiesiac perMiesiac={podsumowanie?.per_miesiac} />
      </div>
    </div>
  );
};

export default KontrolaFakturPage;
